import React, { useState } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp, Activity, Zap } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const DeviationIdentification = ({ searchQuery, filterLocation }) => {
  const deviations = [
    {
      id: 'DEV-001',
      type: 'No Flow',
      node: 'Feed Pipeline',
      description: 'Complete loss of flow in main feed line',
      causes: ['Pump failure', 'Valve closure', 'Line blockage'],
      consequences: ['Process shutdown', 'Production loss', 'Equipment damage'],
      safeguards: ['Flow transmitter', 'Low flow alarm', 'Backup pump'],
      frequency: 'Medium',
      severity: 'High',
      location: 'offshore-alpha',
      equipmentAffected: ['P-101', 'FT-201', 'XV-301'],
    },
    {
      id: 'DEV-002',
      type: 'High Pressure',
      node: 'Compression System',
      description: 'Pressure exceeds design limits',
      causes: ['Relief valve failure', 'Downstream blockage', 'Control system malfunction'],
      consequences: ['Equipment rupture', 'Personnel injury', 'Gas release'],
      safeguards: ['Pressure relief valve', 'High pressure alarm', 'Emergency shutdown'],
      frequency: 'Low',
      severity: 'Critical',
      location: 'offshore-alpha',
      equipmentAffected: ['COMP-01', 'PSV-101', 'PT-102'],
    },
    {
      id: 'DEV-003',
      type: 'Low Temperature',
      node: 'Heat Exchanger',
      description: 'Process temperature below operational limits',
      causes: ['Heating system failure', 'Low flow rate', 'Ambient conditions'],
      consequences: ['Product quality issues', 'Freezing', 'Equipment damage'],
      safeguards: ['Temperature monitoring', 'Low temp alarm', 'Bypass heating'],
      frequency: 'Medium',
      severity: 'Medium',
      location: 'refinery-01',
      equipmentAffected: ['HX-201', 'TT-205'],
    },
    {
      id: 'DEV-004',
      type: 'High Level',
      node: 'Separator Vessel',
      description: 'Liquid level exceeds high limit',
      causes: ['Level control failure', 'Outlet valve malfunction', 'High inlet flow'],
      consequences: ['Liquid carryover', 'Downstream equipment damage', 'Process upset'],
      safeguards: ['Level transmitter', 'High level alarm', 'Level control valve'],
      frequency: 'High',
      severity: 'High',
      location: 'offshore-bravo',
      equipmentAffected: ['V-301', 'LT-302', 'LCV-303'],
    },
  ];

  const deviationTypes = [
    { type: 'No Flow', icon: TrendingDown, count: 12, color: 'text-red-600' },
    { type: 'High Pressure', icon: TrendingUp, count: 8, color: 'text-orange-600' },
    { type: 'Low Temperature', icon: Activity, count: 15, color: 'text-blue-600' },
    { type: 'High Level', icon: Zap, count: 10, color: 'text-purple-600' },
  ];

  const filteredDeviations = deviations?.filter(dev => {
    const matchesSearch = searchQuery === '' || 
      dev?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      dev?.node?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      dev?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesLocation = filterLocation === 'all' || dev?.location === filterLocation;
    
    return matchesSearch && matchesLocation;
  });

  const getSeverityColor = (severity) => {
    const colors = {
      Critical: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors?.[severity] || colors?.Medium;
  };

  return (
    <div className="space-y-6">
      {/* Deviation Type Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {deviationTypes?.map((devType) => {
          const Icon = devType?.icon;
          return (
            <div key={devType?.type} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-6 h-6 ${devType?.color}`} />
                <span className="text-2xl font-bold text-gray-900">{devType?.count}</span>
              </div>
              <div className="text-sm font-medium text-gray-700">{devType?.type}</div>
              <div className="text-xs text-gray-500 mt-1">Identified scenarios</div>
            </div>
          );
        })}
      </div>
      {/* Deviation List */}
      <div className="space-y-4">
        {filteredDeviations?.map((deviation) => (
          <div key={deviation?.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{deviation?.type}</h3>
                    <span className="text-sm text-gray-600">({deviation?.id})</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{deviation?.node}</p>
                </div>
                <div className="flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(deviation?.severity)}`}>
                    {deviation?.severity}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    {deviation?.frequency} Frequency
                  </span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Causes */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Possible Causes
                  </h4>
                  <ul className="space-y-2">
                    {deviation?.causes?.map((cause, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        {cause}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Consequences */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Consequences
                  </h4>
                  <ul className="space-y-2">
                    {deviation?.consequences?.map((consequence, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        {consequence}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Safeguards */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Safeguards
                  </h4>
                  <ul className="space-y-2">
                    {deviation?.safeguards?.map((safeguard, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        {safeguard}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Equipment and Description */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Description</div>
                    <p className="text-sm text-gray-700">{deviation?.description}</p>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Affected Equipment</div>
                    <div className="flex flex-wrap gap-2">
                      {deviation?.equipmentAffected?.map((equip, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                          {equip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeviationIdentification;